import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Json } from '@se/cp.common';
import {
    EExternalIntegrationCallType,
    EOnError,
    EOperator,
    FullIntegrationStoreData,
    IExternalIntegration,
    IExternalIntegrationCall,
} from '../tp-integrations.interface';
import { fillTemplateWithData, modelServiceJsonToIntegrationStoreData } from '../routines';
import { TpIntegrationsCacheService } from '../tp-integrations-cache.service';
import { NotificationService } from '@se/se.ui.html5';

export interface IDialogData {
    id: string;
    data: FullIntegrationStoreData;
    externalIntegrationData: IExternalIntegration;
}

@Component({
    selector: 'se-stack-manager',
    templateUrl: './stack-manager.component.html',
    styleUrls: ['./stack-manager.component.scss'],
})
export class StackManagerComponent implements OnInit {
    dataStore: FullIntegrationStoreData | null = null;
    id: string = '';
    calls: IExternalIntegrationCall[] = [];
    debug: boolean = false;
    currentIndex: number = 0;
    currentCall: IExternalIntegrationCall | null = null;

    constructor(
        private cache: TpIntegrationsCacheService,
        private dialogRef: MatDialogRef<StackManagerComponent>,
        private notificationService: NotificationService,
        @Inject(MAT_DIALOG_DATA) public data: IDialogData,
    ) {
    }

    ngOnInit(): void {
        this.id = this.data.id;
        this.dataStore = this.data.data;
        this.debug = this.data.externalIntegrationData.debug;
        this.calls = this.data.externalIntegrationData.calls;
        this.setCall();
    }

    setCall = () => {
        const currentCall = this.calls[this.currentIndex];
        if (currentCall.cacheKey && currentCall.target && this.dataStore) {
            currentCall.cacheKey = fillTemplateWithData(this.dataStore, currentCall.placeholders, currentCall.cacheKey);
            const cachedData = this.cache.get(currentCall.cacheKey);
            if (cachedData) {
                this.dataStore[currentCall.target] = cachedData as Json;
                this.dataStore = modelServiceJsonToIntegrationStoreData(cachedData as Json, currentCall.target, this.dataStore);

                return this.next();
            }
        }

        if (currentCall.type === EExternalIntegrationCallType.CACHE_OP) {
            console.log('CACHE_OP', this.dataStore, currentCall);
            return this.next();
        }
        this.currentCall = currentCall;
    };

    next = () => {
        this.currentIndex++;
        this.setCall();
    };

    complete = () => {
        this.dataStore = null;
        this.id = '';
        this.calls = [];
        this.currentIndex = 0;
        this.currentCall = null;
        this.dialogRef.close();
    };

    completeWithError = (message: string = 'unrecognized call type') => {
        this.currentIndex = 0;
        this.currentCall = {
            description: 'Error',
            type: EExternalIntegrationCallType.ERROR,
            placeholders: [],
            errorHandling: {
                action: EOnError.BREAK,
                message,
                path: '',
                operator: EOperator.EQUAL,
                valueTrigger: '',
            },
        };
        this.calls = [this.currentCall];
    };

    onCallEnd = (newData: FullIntegrationStoreData) => {

        // store data
        this.dataStore = {
            ...this.dataStore,
            ...newData,
        };

        /* cache management*/
        if (this.currentCall && this.currentCall.cacheKey && this.currentCall.target) {
            const originalResponse = newData[this.currentCall.target];
            this.cache.set(this.currentCall.cacheKey, originalResponse);
        }

        if (this.debug) {
            console.log(`INTEGRATIONS: onCallEnd`, {
                dataStore: this.dataStore,
                newData,
                currentIndex: this.currentIndex,
            });
        }

        //next or complete
        if (this.calls.length - 1 === this.currentIndex) this.complete();
        else this.next();
    };

    onCallError = (errorMessage: string) => {

        if (this.debug) {
            console.log(`INTEGRATIONS: onCallError`, {
                errorMessage,
                currentCall: this.currentCall,
            });
        }

        if (errorMessage === 'FORCE_CLOSE') {
            return this.complete();
        }

        const currentCall = this.currentCall;
        const errorHandling = currentCall?.errorHandling;
        const message = errorMessage && errorHandling?.message ? `${errorMessage} - ${errorHandling?.message}` : `${errorHandling?.message || errorMessage || 'GenericIntegrationError'}`;

        if (errorHandling?.action === EOnError.CONTINUE) {
            this.notificationService.warning(message);
            this.next();
        } /*if (errorAction === EOnError.BREAK) per ora di default facciamo break*/ else {
            this.notificationService.error(message);
            if (message) this.completeWithError(message);
            else this.complete();
        }
    };

    showModalWindowUrlCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.MODAL_WINDOW_URL && index === this.currentIndex;

    showTabWindowUrlCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.TAB_WINDOW_URL && index === this.currentIndex;

    showRestCall = (call: IExternalIntegrationCall, index: number) =>
        (call.type === EExternalIntegrationCallType.REST_EXTERNAL ||
            call.type === EExternalIntegrationCallType.REST_ESTENSA) &&
        index === this.currentIndex;

    showErrorCall = (call: IExternalIntegrationCall, index: number) =>
        !EExternalIntegrationCallType[call.type] || call.type === EExternalIntegrationCallType.ERROR;

    showArrayFindCall = (call: IExternalIntegrationCall, index: number) =>
        call.type === EExternalIntegrationCallType.FIND_IN_DATA && index === this.currentIndex;

    showFormCall = (call: IExternalIntegrationCall, index: number) => call.type === EExternalIntegrationCallType.FORM && index === this.currentIndex;

}

